import { useFetch } from "hooks/api";
import { Row, Col, Table } from "react-bootstrap";
import Loading from "components/core/blocks/Loading";
import moment from "moment";
import { trellisPalette } from "components/custom/analytics/palettes";
import { formatCurrency, formatNumber } from "utils/formatNumber";
import { keepPreviousData } from "@tanstack/react-query";

interface Props {
  productIds: Array<string> | string;
  marketPlace: string;
  adSpend: number;
}

interface Forecast {
  is: number; // Min Ad Spend
  as: number; // Max Ad Spend
  bc: number; // Best Case Units
  wc: number; // Worst Case Units
  e?: boolean; // Evidence
}

interface InventoryResults {
  forecast_units: Forecast[];
  forecast_date: string;
}

const SalesForecast = ({ productIds, marketPlace, adSpend }: Props) => {
  const filters = {
    // Check if productIds is an array or a string, if on type array, join the array to a string
    product_id: Array.isArray(productIds) ? productIds.join(",") : productIds,
  };

  const { data, isLoading } = useFetch<InventoryResults>(
    ["pricing_forecast", productIds],
    `/pricing/forecast`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const UnitPrediction: React.FC<{ adSpend: number }> = ({ adSpend }) => {
    const dailySpend = adSpend;
    const forecast = data?.forecast_units.find((f) => dailySpend >= f.is && dailySpend <= f.as);

    return (
      <span>
        You have spent {formatCurrency(adSpend, marketPlace, false, false, true)} over the past month which should
        generate between {formatNumber(forecast?.wc)} and {formatNumber(forecast?.bc)} units in the next 30 days.
      </span>
    );
  };

  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  return (
    <Row className="mt-2 p-3">
      <Col className="mb-4" xs={12}>
        <div
          style={{
            padding: "2rem",
            border: "1px solid rgb(238, 238, 238)",
            borderRadius: "6px",
            backgroundColor: "rgb(248, 248, 248)",
          }}
        >
          <div>
            The following table shows the best and worst case screnarios for the number of units that will be sold
            depending on the ad spend budget that you will have in the 30 days following the forecast date.
          </div>

          <table className="mt-4">
            <tr>
              <td style={{ whiteSpace: "nowrap" }} className="pe-3">
                <strong>How to Interpret This Table:</strong>
              </td>
              <td className="pe-3">
                {" "}
                If your Monthly PPC spend falls between{" "}
                {data &&
                  data.forecast_units[1] &&
                  formatCurrency(data.forecast_units[1].is, marketPlace, true, false, true)}{" "}
                and{" "}
                {data &&
                  data.forecast_units[1] &&
                  formatCurrency(data.forecast_units[1].as, marketPlace, true, false, true)}
                , you can expect to generate {data && data.forecast_units[1] && formatNumber(data.forecast_units[1].wc)}{" "}
                to {data && data.forecast_units[1] && formatNumber(data.forecast_units[1].bc)} unit sales over a 30-day
                period.
              </td>
            </tr>
            <tr>
              <td className="pe-3">
                <strong>Forecast Date?</strong>
              </td>
              <td className="pe-3 pt-4">
                {" "}
                The forecast date is the date on which the forecast was generated. The forecast is based on the data
                available up to this date. Forecast are generated on a monthly basis.
              </td>
            </tr>
            <tr>
              <td className="pe-3 pt-4">
                <span style={{ backgroundColor: trellisPalette[2], borderRadius: "5px" }}>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                </span>
              </td>
              <td className="pe-3 pt-4"> We have historical evidence which supports this forecast. </td>
            </tr>
          </table>
        </div>
      </Col>
      <Col className="px-4" xs={4}>
        <Row>
          <Col>
            <span className="fs-4 fw-bold">Anyalysis</span>
          </Col>
        </Row>
        <div
          style={{
            padding: "2rem",
            border: "1px solid rgb(238, 238, 238)",
            borderRadius: "6px",
            backgroundColor: "rgb(248, 248, 248)",
          }}
          className="mt-4 pt-4 fs-4"
        >
          <UnitPrediction adSpend={adSpend} />
        </div>
      </Col>
      <Col className="px-4" xs={8}>
        <Row>
          <Col>
            <span className="fs-4 fw-bold">Sales Forecast</span>
          </Col>
          <Col className="text-end">
            <span className="fs-4">Forecast Generated on: {moment(data?.forecast_date).format("MMM DD, YYYY")}</span>
          </Col>
        </Row>
        <Table>
          <thead>
            <tr>
              <th style={{ width: "1%" }}>&nbsp;</th>
              <th>Min Monthly Ad Spend</th>
              <th>Max Monthly Ad Spend</th>
              <th>Worst Monthly Case Units</th>
              <th>Best Monthly Case Units</th>
            </tr>
          </thead>
          <tbody>
            {data?.forecast_units.map((forecast) => (
              <tr key={forecast.is}>
                <td style={{ width: "1%" }} className="text-center">
                  <span style={{ backgroundColor: forecast.e ? trellisPalette[2] : "white", borderRadius: "5px" }}>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                </td>
                <td className="text-center">{formatCurrency(forecast.is, marketPlace, true, false, true)}</td>
                <td className="text-center">{formatCurrency(forecast.as, marketPlace, true, false, true)}</td>
                <td className="text-center">{formatNumber(forecast.wc)}</td>
                <td className="text-center">{formatNumber(forecast.bc)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

export default SalesForecast;
